/**
 * The editor's form copy of a run definition (#169 slice 3). The YAML text
 * is the document: the form starts from preview's parse of it
 * (`fieldsFrom`), and each form change goes back through the backend's
 * render (`definitionFrom`), so the frontend never parses or writes YAML.
 * The backend validates every bound; these only reshape.
 *
 * The one exception is the new-definition template, a fixed text, and
 * `nameInYaml`, which finds a new definition's name for preview to lint it
 * under (the backend still checks the name against the file name).
 */

import { DEFINITION_NAME_PATTERN } from './definitionName';
import type { BranchCategory, RunDefinition } from '../types/projectPanel.types';

/** Backend `definitions.ALL_BRANCHES`, in its order (the default). */
export const BRANCH_CATEGORIES: readonly BranchCategory[] = ['default', 'protected', 'feature', 'merge_request'];

export const TEMPLATE_INSTRUCTIONS = 'Fix the cause of the failure in the code, not the check.';

export type AgentChoice = '' | 'pi' | 'claude-code';

/** The form's inputs, as typed (numbers as text; '' means unset). */
export interface DefinitionFields {
  name: string;
  /** `on.job_failed`, one pattern per line. */
  patterns: string;
  branches: BranchCategory[];
  priority: string;
  /** '' is the default agent (Pi). */
  agent: AgentChoice;
  model: string;
  instructions: string;
  /** `input.report.path`; the format is always grype. */
  reportPath: string;
  budgetMinutes: string;
  budgetTokens: string;
}

/** A new definition's text: its name, one empty pattern, one instructions line. */
export function definitionTemplate(name: string): string {
  const quoted = name === '' ? "''" : name;
  return `name: ${quoted}\non:\n  job_failed:\n  - ''\ninstructions: ${TEMPLATE_INSTRUCTIONS}\n`;
}

/** The template, as the form shows it. */
export function templateFields(): DefinitionFields {
  return {
    name: '',
    patterns: '',
    branches: [...BRANCH_CATEGORIES],
    priority: '',
    agent: '',
    model: '',
    instructions: TEMPLATE_INSTRUCTIONS,
    reportPath: '',
    budgetMinutes: '',
    budgetTokens: '',
  };
}

export function fieldsFrom(d: RunDefinition): DefinitionFields {
  return {
    name: d.name,
    patterns: d.on.job_failed.join('\n'),
    branches: d.on.branches ?? [...BRANCH_CATEGORIES],
    priority: d.priority === undefined || d.priority === 0 ? '' : String(d.priority),
    agent: d.agent ?? '',
    model: d.model ?? '',
    instructions: d.instructions ?? '',
    reportPath: d.input?.report?.path ?? '',
    budgetMinutes: d.budget ? String(d.budget.minutes) : '',
    budgetTokens: d.budget ? String(d.budget.tokens) : '',
  };
}

function blankToNull(value: string): string | null {
  return value.trim() === '' ? null : value;
}

/** An unset half of the budget is NaN (JSON null): the backend names it. */
function integer(value: string): number {
  return value.trim() === '' ? Number.NaN : Number(value);
}

export function definitionFrom(f: DefinitionFields): RunDefinition {
  const patterns = f.patterns.split('\n').map((p) => p.trim()).filter((p) => p !== '');
  const report = f.reportPath.trim();
  const noBudget = f.budgetMinutes.trim() === '' && f.budgetTokens.trim() === '';
  return {
    name: f.name.trim(),
    on: { job_failed: patterns, branches: BRANCH_CATEGORIES.filter((b) => f.branches.includes(b)) },
    ...(f.priority.trim() !== '' ? { priority: Number(f.priority) } : {}),
    agent: f.agent === '' ? null : f.agent,
    model: blankToNull(f.model.trim()),
    instructions: blankToNull(f.instructions),
    input: report === '' ? null : { report: { path: report, format: 'grype' } },
    budget: noBudget ? null : { minutes: integer(f.budgetMinutes), tokens: integer(f.budgetTokens) },
  };
}

const NAME_LINE = new RegExp(
  `^name:[ \\t]*(['"]?)(${DEFINITION_NAME_PATTERN.source.slice(1, -1)})\\1[ \\t]*(?:#.*)?$`,
  'm',
);

/** The top-level `name:` line's value when it is a valid definition name. */
export function nameInYaml(text: string): string | null {
  return NAME_LINE.exec(text)?.[2] ?? null;
}
